// resources/js/Pages/HallOfFame/hooks/useFilters.js
import { useState, useMemo } from 'react';

export function useFilters(scripts) {
    const [selectedGenres, setSelectedGenres] = useState([]);
    const [selectedScriptTypes, setSelectedScriptTypes] = useState([]);
    
    // Extract unique genres from genres_taxonomy
    const genres = useMemo(() => {
        const genreSet = new Set();
        
        // Debug: Log first script to see structure
        if (scripts.length > 0) {
            console.log('[useFilters Debug] First script:', scripts[0]);
            console.log('[useFilters Debug] genres_taxonomy type:', typeof scripts[0].genres_taxonomy);
            console.log('[useFilters Debug] genres_taxonomy value:', scripts[0].genres_taxonomy);
        }
        
        // Recursive function to extract Super-genres from the tree
        const extractSuperGenres = (node) => {
            if (!node) return;
            
            // If this is a Super-genre, add it
            if (node.type === 'Super-genre' && node.name) {
                genreSet.add(node.name);
            }
            
            // Recurse into children
            if (node.children && Array.isArray(node.children)) {
                node.children.forEach(child => extractSuperGenres(child));
            }
        };
        
        scripts.forEach(script => {
            let taxonomy = script.genres_taxonomy;
            
            // Parse if it's a JSON string
            if (typeof taxonomy === 'string') {
                try {
                    taxonomy = JSON.parse(taxonomy);
                } catch (e) {
                    console.error('Failed to parse genres_taxonomy:', e);
                    return;
                }
            }
            
            // Start extraction from the root
            if (taxonomy) {
                extractSuperGenres(taxonomy);
            }
        });
        
        const genreArray = Array.from(genreSet).sort();
        console.log('[useFilters Debug] Extracted genres:', genreArray);
        
        // Convert to array and sort alphabetically
        return genreArray;
    }, [scripts]);
    
    // Extract unique script types
    const scriptTypes = useMemo(() => {
        const typeSet = new Set();
        
        scripts.forEach(script => {
            if (script.script_type) {
                typeSet.add(script.script_type);
            }
        });
        
        return Array.from(typeSet).sort();
    }, [scripts]);
    
    // Filter scripts based on selections, then sort by genre coverage when filters active
    const filteredScripts = useMemo(() => {
        // Helper: extract Super-genres with their coverage % from taxonomy tree
        const getSuperGenresWithCoverage = (node) => {
            const genres = {}; // { genreName: coveragePercent }

            if (!node) return genres;

            if (node.type === 'Super-genre' && node.name) {
                genres[node.name] = parseInt(node.coverage) || 0;
            }

            if (node.children && Array.isArray(node.children)) {
                node.children.forEach(child => {
                    Object.assign(genres, getSuperGenresWithCoverage(child));
                });
            }

            return genres;
        };

        // Helper: parse taxonomy once
        const parseTaxonomy = (script) => {
            let taxonomy = script.genres_taxonomy;
            if (typeof taxonomy === 'string') {
                try {
                    taxonomy = JSON.parse(taxonomy);
                } catch (e) {
                    return null;
                }
            }
            return taxonomy;
        };

        let results = scripts.filter(script => {
            // Genre filter - AND logic (must have ALL selected genres)
            if (selectedGenres.length > 0) {
                const taxonomy = parseTaxonomy(script);
                if (!taxonomy) return false;

                const scriptGenres = getSuperGenresWithCoverage(taxonomy);

                // Check if script has ALL of the selected genres (AND logic)
                const hasAllGenres = selectedGenres.every(selectedGenre =>
                    selectedGenre in scriptGenres
                );

                if (!hasAllGenres) {
                    return false;
                }
            }

            // Script type filter
            if (selectedScriptTypes.length > 0) {
                if (!selectedScriptTypes.includes(script.script_type)) {
                    return false;
                }
            }

            return true;
        });

        // When genre filters are active, sort by total coverage of selected genres (highest first)
        if (selectedGenres.length > 0) {
            results = [...results].sort((a, b) => {
                const aGenres = getSuperGenresWithCoverage(parseTaxonomy(a));
                const bGenres = getSuperGenresWithCoverage(parseTaxonomy(b));

                // Sum coverage % for all selected genres
                const aCoverage = selectedGenres.reduce((sum, g) => sum + (aGenres[g] || 0), 0);
                const bCoverage = selectedGenres.reduce((sum, g) => sum + (bGenres[g] || 0), 0);

                return bCoverage - aCoverage; // Descending — highest coverage first
            });
        }

        return results;
    }, [scripts, selectedGenres, selectedScriptTypes]);
    
    const clearFilters = () => {
        setSelectedGenres([]);
        setSelectedScriptTypes([]);
    };
    
    return {
        genres,
        scriptTypes,
        selectedGenres,
        selectedScriptTypes,
        filteredScripts,
        setSelectedGenres,
        setSelectedScriptTypes,
        clearFilters
    };
}